import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  Paper,
  TextField,
  Alert,
  Divider,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';

const CARD_BG = '#0b1522';
const SURFACE = '#0e1a2b';
const BORDER = 'rgba(120,140,180,.18)';
const SUB_BORDER = 'rgba(120,140,180,.14)';
const TEXT_MAIN = '#eaf2ff';
const TEXT_SUB = '#9fb4d8';
const PRIMARY_GRAD = 'linear-gradient(90deg,#3ec7ff,#447dff)';

const Card = styled(Paper)({
  borderRadius: 18,
  padding: 16,
  border: `1px solid ${BORDER}`,
  background: CARD_BG,
  boxShadow: '0 18px 40px rgba(8,20,40,.45)',
});

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: SURFACE,
    color: TEXT_MAIN,
    '& fieldset': { borderColor: BORDER },
    '&:hover fieldset': { borderColor: '#7fb0ff' },
    '&.Mui-focused fieldset': {
      borderColor: '#7fb0ff',
      boxShadow: '0 0 0 3px rgba(127,176,255,.18)',
    },
    '& input': { color: TEXT_MAIN },
  },
  '& .MuiInputLabel-root': { color: TEXT_SUB },
  '& .MuiInputLabel-root.Mui-focused': { color: '#b8d8ff' },
  '& .MuiFormHelperText-root': { marginLeft: 0, color: TEXT_SUB },
};

const btnPrimary = {
  height: 44,
  borderRadius: 12,
  fontWeight: 900,
  textTransform: 'none' as const,
  color: '#0b1522',
  background: PRIMARY_GRAD,
  border: '1px solid rgba(62,199,255,.4)',
  boxShadow: '0 8px 20px rgba(62,199,255,.25)',
  '&:hover': {
    filter: 'brightness(1.05)',
    boxShadow: '0 10px 24px rgba(62,199,255,.28)',
  },
};

const btnOutline = {
  height: 44,
  borderRadius: 12,
  fontWeight: 800,
  textTransform: 'none' as const,
  color: TEXT_MAIN,
  border: `1px solid ${BORDER}`,
  background: 'transparent',
  '&:hover': { borderColor: '#7fb0ff', boxShadow: '0 6px 16px rgba(16,24,40,.25)' },
};

const isValidEmailFormat = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

interface EmailChangeDialogProps {
  open: boolean;
  onClose: () => void;
  currentEmail: string;
  loading?: boolean;
  successMessage?: string | null;
  errorMessage?: string | null;
  onSubmit: (newEmail: string) => Promise<void> | void;
}

const EmailChangeDialog: React.FC<EmailChangeDialogProps> = ({
  open,
  onClose,
  currentEmail,
  loading = false,
  successMessage = null,
  errorMessage = null,
  onSubmit,
}) => {
  const [newEmail, setNewEmail] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setNewEmail('');
    setLocalError(null);
  }, [open]);

  const handleSubmit = async () => {
    setLocalError(null);
    const email = newEmail.trim();

    if (!email) {
      setLocalError('새 이메일 주소를 입력하세요.');
      return;
    }
    if (!isValidEmailFormat(email)) {
      setLocalError('올바른 이메일 형식이 아닙니다.');
      return;
    }
    if (email === currentEmail) {
      setLocalError('현재 이메일과 동일합니다.');
      return;
    }

    await onSubmit(email);
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ elevation: 0, sx: { borderRadius: 0, background: 'transparent' } }}
      BackdropProps={{ sx: { backgroundColor: 'rgba(0,0,0,.55)' } }}
    >
      <Card>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography sx={{ color: TEXT_MAIN, fontWeight: 900, fontSize: 18 }}>
            이메일 변경
          </Typography>
          <IconButton onClick={onClose} sx={{ color: TEXT_SUB }} disabled={loading}>
            <CloseRoundedIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ px: 0, pt: 0.5, pb: 0 }}>
          <Box
            sx={{
              p: 1.25,
              borderRadius: 2,
              border: `1px solid ${SUB_BORDER}`,
              background: alpha('#0e1a2b', 0.7),
              mb: 2,
            }}
          >
            <Typography sx={{ color: TEXT_MAIN, fontWeight: 900, fontSize: 13, mb: 0.75 }}>
              현재 이메일
            </Typography>
            <Typography sx={{ color: TEXT_SUB, fontSize: 12.5 }}>
              {currentEmail || '-'}
            </Typography>
          </Box>

          <Alert severity="info" icon={<EmailRoundedIcon />} sx={{ mb: 2, borderRadius: 2 }}>
            실제 운영에서는 새 이메일로 인증 링크를 발송한 뒤, 인증 완료 시 변경 처리하는 방식이 안전합니다.
          </Alert>

          <TextField
            fullWidth
            label="새 이메일 주소"
            placeholder="example@domain.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            sx={fieldSx}
            disabled={loading}
          />

          {localError && (
            <Alert severity="error" sx={{ mt: 1.25, borderRadius: 2 }}>
              {localError}
            </Alert>
          )}

          {errorMessage && (
            <Alert severity="error" sx={{ mt: 1.25, borderRadius: 2 }}>
              {errorMessage}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" sx={{ mt: 1.25, borderRadius: 2 }}>
              {successMessage}
            </Alert>
          )}

          <Divider sx={{ my: 2, borderColor: SUB_BORDER }} />

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose} disabled={loading} fullWidth sx={btnOutline}>
              취소
            </Button>
            <Button onClick={handleSubmit} disabled={loading} fullWidth sx={btnPrimary}>
              {loading ? '처리 중…' : '이메일 변경 요청'}
            </Button>
          </Box>
        </DialogContent>
      </Card>
    </Dialog>
  );
};

export default EmailChangeDialog;